import "../paged-table.scss";
import { ComplianceDocument, DashboardComplianceItem } from "../../../../models/compliance";
import { caregiverStatus } from "../../../../scripts/consts/commonConsts";
import { CaregiverStatus } from "../../../../scripts/messages/caregiver";
import {
  ComplianceDashboardPageRequest,
  ComplianceDashboardSortField,
  MessageComplianceDashboardCaregiver,
} from "../../../../scripts/messages/compliance";
import { CaregiverDocumentTypeId, CaregiverId, OfficeId } from "../../../../scripts/messages/ids";
import { ComplianceParser } from "../../../../parsers/compliance-parser";
import { CompService } from "../../comp.service";
import { LabelColor } from "../../../../scripts/utils/caregiverUtils";
import { groupBy } from "../../../../scripts/utils/generalUtils";
import "../compliance-dashboard/compliance-dashboard.component.scss";
import { MultiselectCallbackParameter } from "../../../shared/components/multiselect/multiselect.component";

/**
 * The Compliance page's Caregiver Status tab, one page at a time.
 *
 * The tab it replaces was built from `got_compliance_data` - the whole-agency
 * dataset the old dashboard broadcast: every caregiver in the agency joined
 * against every compliance item in the agency, then filtered, sorted and paged
 * in the browser. On the largest agency that is 213,529 caregivers and about
 * 356 MB of JSON to render twenty-five rows.
 *
 * This asks the server for the rows on screen. It shares the endpoint with the
 * paged dashboard, because both answer the same question - a page of caregivers
 * with their compliance items - and differ only in what they render.
 *
 * The consequence to hold onto when changing it: there is no local collection.
 * A filter, a sort or a column needing data the page was not sent has to be
 * added to the endpoint. In particular the five *item-level* filters here are
 * resolved server-side into a caregiver id set, because no amount of caregiver
 * data can answer them.
 */

type ComplianceLabel = "Compliant" | "Not compliant";

/** What the row's expiry column shows, derived from the caregiver's items. */
interface ExpirySummary {
  shouldDisplay: boolean;
  isValid: boolean;
  text: string;
  /** Days, for the column's sort. Absent expiry sorts last. */
  sortValue: number;
}

interface StatusRow {
  caregiver: MessageComplianceDashboardCaregiver;
  items: DashboardComplianceItem[];
  /** Non-compliant items only - what the drill-down lists. */
  incompliances: DashboardComplianceItem[];
  complianceStatus: ComplianceLabel;
  statusText: string;
  statusColor: string;
  nextStatusText: string | null;
  confirmed: "installed" | "uninstalled" | "notinstalled";
  expiry: ExpirySummary;
  isExpanded: boolean;
}

const PAGE_SIZES = [10, 25, 50, 100];

/** Item states that count as compliant, mirroring `complianceService`. */
const COMPLIANT_ITEM_STATUSES = new Set(["Compliant", "Resolved", "Not due yet"]);

/**
 * Which column headers the server can order the whole result set by.
 *
 * Deliberately short. The other columns - languages, certifications, days to
 * expire, installation - are derived from the caregiver's *items* or from
 * multi-valued fields, and none is in the search index or the cache's sort
 * ranks, so the server cannot order the whole set by them. Sorting only the
 * loaded page would look like sorting everything, so those headers are inert.
 */
const SORTABLE_COLUMNS: Record<string, ComplianceDashboardSortField> = {
  "Caregiver Name": "name",
  "Caregiver ID": "displayId",
  "Compliance Status": "isCompliant",
};

//! @ngInject
class CaregiverStatusPagedCtrl implements ng.IComponentController {
  public rows: StatusRow[] = [];
  public totalCount = 0;
  public isTruncated = false;
  public isLoading = true;

  /**
   * A refetch with nothing on screen yet - the only time the table is replaced
   * by a shimmer. Every later one keeps the table and dims it, so a page turn
   * or a filter change does not collapse the page and lose the scroll position.
   */
  public isFirstLoad = () => this.isLoading && this.rows.length === 0;

  /** A refetch with rows still on screen. */
  public isReloading = () => this.isLoading && this.rows.length > 0;

  public offset = 0;
  public limit = 25;
  public readonly pageSizes = PAGE_SIZES;

  private sortField: ComplianceDashboardSortField = "name";
  private sortOrder: "asc" | "desc" = "asc";
  public sortedColumn: string | null = "Caregiver Name";

  public documentTypes: ComplianceDocument[] = [];

  /** The tab's own filter state, independent of the dashboard's. */
  public filters = {
    freeText: "",
    statuses: [] as { id: string; label?: string }[],
    certifications: [] as { id: string; label?: string }[],
    offices: [] as { id: number; label?: string }[],
    complianceStatus: [] as { id: string }[],
    itemStatuses: [] as { id: string; label?: string }[],
    expiryFrom: null as string | null,
    expiryTo: null as string | null,
    includeItemsWithIssues: true,
    issueDocumentTypeIds: [] as CaregiverDocumentTypeId[],
    failedReviews: [] as { id: string }[],
    declinedBy: [] as { id: string }[],
  };

  public statusOptions = caregiverStatus.dropdown;
  public complianceStatusOptions = ["Compliant", "Not compliant"].map((s) => ({
    id: s,
    label: s,
  }));

  constructor(
    private compService: CompService,
    private toaster: toaster.IToasterService,
    private $rootScope: ng.IRootScopeService,
    private complianceService: any
  ) {}

  $onInit = () => {
    this.load();
  };

  /* ------------------------------------------------------------- loading -- */

  private buildRequest = (): ComplianceDashboardPageRequest => {
    const search = this.filters.freeText.trim();

    return {
      search: search === "" ? null : search,
      statuses: this.filters.statuses.map((s) => s.id as CaregiverStatus),
      certifications: this.filters.certifications.map(
        (c) => c.id as unknown as ComplianceDashboardPageRequest["certifications"][number]
      ),
      officeIds: this.filters.offices.map((o) => o.id as unknown as OfficeId),
      // A yes/no pair is only a filter when exactly one side is chosen.
      isCompliant:
        this.filters.complianceStatus.length === 1
          ? this.filters.complianceStatus[0].id === "Compliant"
          : null,
      isFromLandingPage: null,
      hasHireDate: null,
      caregiverIds: null,

      itemStatuses: this.filters.itemStatuses.map((s) => s.id),
      expiryFrom: this.filters.expiryFrom,
      expiryTo: this.filters.expiryTo,
      includeItemsWithIssues: this.filters.includeItemsWithIssues,
      issueDocumentTypeIds: this.filters.issueDocumentTypeIds,
      failedReviews: this.filters.failedReviews.map((f) => f.id),
      declinedBy: this.filters.declinedBy.map((d) => d.id),

      sortField: this.sortField,
      sortOrder: this.sortOrder,
      limit: this.limit,
      offset: this.offset,
    };
  };

  public load = () => {
    this.isLoading = true;

    this.compService
      .fetchDashboardPage(this.buildRequest())
      .then((data) => {
        const followups = ComplianceParser.getFollowupDocumentTypesMap(data.documents);
        const documentsMap = ComplianceParser.parseComplianceDocumentMap(data.documents, followups);

        this.documentTypes = data.documents.map((document) =>
          ComplianceParser.parseComplianceDocument(document, followups)
        );

        this.rows = data.rows.map((row) => {
          const items = row.items.map((item) =>
            ComplianceParser.parseDashboardComplianceItem(item, documentsMap)
          );

          return {
            caregiver: row.caregiver,
            items,
            incompliances: items.filter((item) => !COMPLIANT_ITEM_STATUSES.has(item.status)),
            complianceStatus: row.caregiver.isCompliant ? "Compliant" : "Not compliant",
            statusText: this.statusTextOf(row.caregiver.status),
            statusColor: this.statusColorOf(row.caregiver.status),
            nextStatusText:
              row.caregiver.nextStatus === null
                ? null
                : this.statusTextOf(row.caregiver.nextStatus.status),
            confirmed: this.installationOf(row.caregiver),
            expiry: this.summariseExpiry(items),
            isExpanded: false,
          };
        });

        this.totalCount = data.totalCount;
        this.isTruncated = data.isTruncated;
      })
      .catch((err) => {
        console.error(err);
        this.toaster.error("Could not load caregiver compliance");
      })
      .finally(() => {
        this.isLoading = false;
      });
  };

  /** Any filter change is a new result set, so it starts at the first page. */
  private reload = () => {
    this.offset = 0;
    this.load();
  };

  /* -------------------------------------------------------------- derived -- */

  private statusTextOf = (status: CaregiverStatus): string => {
    const match = caregiverStatus.dropdown.find((option: { id: string }) => option.id === status);

    return match?.label ?? String(status);
  };

  private statusColorOf = (status: CaregiverStatus): string => {
    switch (status) {
      case "ACTIVE":
        return "green";
      case "ON_HOLD":
      case "ON_LEAVE":
      case "PENDING":
        return "yellow";
      default:
        return "red";
    }
  };

  private installationOf = (
    caregiver: MessageComplianceDashboardCaregiver
  ): "installed" | "uninstalled" | "notinstalled" => {
    if (caregiver.appInstalled) {
      return "installed";
    }

    // The server already decides installed / never-installed for its filters;
    // here only "has a heartbeat at some point" separates the other two.
    return caregiver.lastSeen === null ? "notinstalled" : "uninstalled";
  };

  /**
   * The soonest expiry or due date across the caregiver's items.
   *
   * Uses the same `complianceService` helper the whole-agency tab used, so the
   * text and the red/green threshold match rather than being reimplemented.
   */
  private summariseExpiry = (items: DashboardComplianceItem[]): ExpirySummary => {
    let soonest: { days: number; text: string; valid: boolean } | null = null;

    for (const item of items) {
      if (COMPLIANT_ITEM_STATUSES.has(item.status) && item.expiryDate === null) {
        continue;
      }

      const params = this.complianceService.getExpiryOrDueDateParams(
        item.status,
        item.dueDate,
        item.expiryDate,
        item.documentType?.requireReVerification ?? false
      );

      const days = params?.data?.daysToExpireSort;

      if (typeof days !== "number" || isNaN(days)) {
        continue;
      }

      if (soonest === null || days < soonest.days) {
        soonest = {
          days,
          text: params.data.daysToExpireText ?? "",
          valid: params.data.daysToExpireTextColor === "green",
        };
      }
    }

    if (soonest === null) {
      return { shouldDisplay: false, isValid: true, text: "", sortValue: Number.MAX_SAFE_INTEGER };
    }

    return {
      shouldDisplay: true,
      isValid: soonest.valid,
      text: soonest.text,
      sortValue: soonest.days,
    };
  };

  /* -------------------------------------------------------------- filters -- */

  public onSearchChange = () => this.reload();

  public onStatusSelect = (selected: MultiselectCallbackParameter<string>) => {
    this.filters.statuses = selected.map((s) => ({ id: String(s.id) }));
    this.reload();
  };

  public onCertificationSelect = (selected: MultiselectCallbackParameter<string>) => {
    this.filters.certifications = selected.map((c) => ({ id: String(c.id) }));
    this.reload();
  };

  public onOfficeSelect = (selected: MultiselectCallbackParameter<number>) => {
    this.filters.offices = selected.map((o) => ({ id: Number(o.id) }));
    this.reload();
  };

  public onComplianceStatusSelect = (selected: MultiselectCallbackParameter<string>) => {
    this.filters.complianceStatus = selected.map((s) => ({ id: String(s.id) }));
    this.reload();
  };

  public onItemStatusSelect = (selected: MultiselectCallbackParameter<string>) => {
    this.filters.itemStatuses = selected.map((s) => ({ id: String(s.id) }));
    this.reload();
  };

  public onExpiryRangeChange = (from: string | null, to: string | null) => {
    // Both ends or neither: a half-open window is not a filter the server
    // applies, matching the page's own `hasStartAndEndDate` rule.
    this.filters.expiryFrom = from;
    this.filters.expiryTo = to;
    this.reload();
  };

  public onIncludeItemsWithIssuesChange = () => this.reload();

  public onIssueSelect = (selected: MultiselectCallbackParameter<CaregiverDocumentTypeId>) => {
    // Expanded to the selected types *and their descendants*, by id. The
    // whole-agency tab matched on document type display names, which are
    // per-agency overrides; ids do not have that problem.
    const chosen = new Set(selected.map((entry) => String(entry.id)));
    const wanted = new Set<string>();

    const addWithDescendants = (id: string) => {
      if (wanted.has(id)) {
        return;
      }

      wanted.add(id);

      for (const document of this.documentTypes) {
        if (String(document.id) === id) {
          for (const child of document.children) {
            addWithDescendants(String(child));
          }
        }
      }
    };

    chosen.forEach(addWithDescendants);

    this.filters.issueDocumentTypeIds = [...wanted].map(
      (id) => Number(id) as unknown as CaregiverDocumentTypeId
    );
    this.reload();
  };

  public onFailedReviewSelect = (selected: MultiselectCallbackParameter<string>) => {
    this.filters.failedReviews = selected.map((entry) => ({ id: String(entry.id) }));
    this.reload();
  };

  public onDeclinedBySelect = (selected: MultiselectCallbackParameter<string>) => {
    this.filters.declinedBy = selected.map((entry) => ({ id: String(entry.id) }));
    this.reload();
  };

  /* --------------------------------------------------------------- sorting -- */

  public isSortable = (columnTitle: string) => SORTABLE_COLUMNS[columnTitle] !== undefined;

  public sortDirection = (columnTitle: string): "asc" | "desc" | null => {
    if (SORTABLE_COLUMNS[columnTitle] !== this.sortField) {
      return null;
    }

    return this.sortOrder;
  };

  public onClickSort = (columnTitle: string) => {
    const field = SORTABLE_COLUMNS[columnTitle];

    if (field === undefined) {
      return;
    }

    this.sortOrder = this.sortField === field && this.sortOrder === "asc" ? "desc" : "asc";
    this.sortField = field;
    this.sortedColumn = columnTitle;

    // Ordering changes which rows are on the page, so the server answers again
    // rather than the page reordering what it happens to hold.
    this.offset = 0;
    this.load();
  };

  /* --------------------------------------------------------------- paging -- */

  public get pageNumber(): number {
    return Math.floor(this.offset / this.limit) + 1;
  }

  public get pageCount(): number {
    return Math.max(1, Math.ceil(this.totalCount / this.limit));
  }

  public get rangeStart(): number {
    return this.totalCount === 0 ? 0 : this.offset + 1;
  }

  public get rangeEnd(): number {
    return Math.min(this.offset + this.limit, this.totalCount);
  }

  public canGoPrevious = () => this.offset > 0;

  public canGoNext = () => this.offset + this.limit < this.totalCount;

  public previousPage = () => {
    if (!this.canGoPrevious()) {
      return;
    }

    this.offset = Math.max(0, this.offset - this.limit);
    this.load();
  };

  public nextPage = () => {
    if (!this.canGoNext()) {
      return;
    }

    this.offset += this.limit;
    this.load();
  };

  public setPageSize = (size: number) => {
    if (size === this.limit) {
      return;
    }

    this.limit = size;
    this.offset = 0;
    this.load();
  };

  /* ------------------------------------------------------------------ rows -- */

  /**
   * Expands one row's issues.
   *
   * The items are already on the row - they arrived with the page - so this
   * opens a drawer rather than fetching anything.
   */
  public toggleRow = (row: StatusRow) => {
    const wasExpanded = row.isExpanded;

    for (const other of this.rows) {
      other.isExpanded = false;
    }

    row.isExpanded = !wasExpanded;
  };

  public openCaregiverModal = (id: CaregiverId) => this.$rootScope.openCaregiverModal(id);

  public officesText = (row: StatusRow) => row.caregiver.officeNames.join(", ");

  public itemStatusColor = (item: DashboardComplianceItem): LabelColor => {
    switch (item.status) {
      case "Compliant":
        return "green";
      case "Not due yet":
      case "Pending Uploads":
        return "orange";
      case "Resolved":
      case "Not required":
        return "gray";
      default:
        return "red";
    }
  };

  /**
   * Document types grouped by section, for the Issues filter's options.
   *
   * Cached against the array it was built from. The template binds
   * `options="ctrl.issueOptions()"`, and AngularJS re-evaluates that every
   * digest: returning a freshly built array each time meant a new identity
   * every pass, the watcher never settled, and the page died with
   * "[$rootScope:infdig] 10 $digest() iterations reached". Same value, same
   * reference, until the document types themselves change.
   */
  private cachedIssueOptions: {
    source: CaregiverStatusPagedCtrl["documentTypes"];
    value: { id: CaregiverDocumentTypeId; label: string }[];
  } | null = null;

  public issueOptions = () => {
    if (this.cachedIssueOptions === null || this.cachedIssueOptions.source !== this.documentTypes) {
      this.cachedIssueOptions = {
        source: this.documentTypes,
        value: [
          ...groupBy(
            this.documentTypes.filter((type) => type.isRoot),
            "section"
          ),
        ].flatMap(([, types]) => types.map((type) => ({ id: type.id, label: type.name }))),
      };
    }

    return this.cachedIssueOptions.value;
  };
}

interface Component extends angular.IComponentOptions {
  $name: string;
}

export const caregiverStatusPagedComponent: Component = {
  $name: "caregiverStatusPaged",
  templateUrl:
    "admin/modules/compliance/components/caregiver-status-paged/caregiver-status-paged.component.html",
  controller: CaregiverStatusPagedCtrl,
  controllerAs: "ctrl",
};
